import { useRef, useMemo, useEffect } from 'react'
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { cn } from '@/lib/utils'
import { sortByFilename } from './constants'
import { BOARD_FILE_SEARCH_ATTR } from './boardFileSearchShortcut'
import type { DocumentPosition } from '@/service/api'

export interface BoardFileSearchProps {
  inputRef?: React.RefObject<HTMLInputElement>
  className?: string
  onClose?: () => void
}

/**
 * Board-level file search: top-left on the board, aligned with the floating toolbar.
 * Typing highlights file names on document cards. Cmd+F / Ctrl+F opens the search.
 * Up/Down arrows or Enter navigate through matches and pan the canvas to each one.
 */
export default function BoardFileSearch({ inputRef: externalRef, className, onClose }: BoardFileSearchProps) {
  const { t } = useTranslation('board')
  const boardFileSearchQuery = useAppStore((s) => s.boardFileSearchQuery)
  const setBoardFileSearchQuery = useAppStore((s) => s.setBoardFileSearchQuery)
  const currentIndex = useAppStore((s) => s.boardFileSearchMatchIndex)
  const setCurrentIndex = useAppStore((s) => s.setBoardFileSearchMatchIndex)
  const documents = useAppStore((s) => s.documents)
  const connectors = useAppStore((s) => s.connectors)
  const renamedDocNames = useAppStore((s) => s.renamedDocNames)
  const selectedDocuments = useAppStore((s) => s.selectedDocuments)
  const internalRef = useRef<HTMLInputElement>(null)
  const inputRef = externalRef ?? internalRef

  // Focus is driven by a store nonce rather than by mount alone: the panel is
  // already mounted when the shortcut fires a second time, so there would be no
  // state change to react to. Selecting the text means re-triggering replaces
  // the previous query, which is what every find-in-page does.
  const focusNonce = useAppStore((s) => s.boardFileSearchFocusNonce)
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.focus()
    el.select()
  }, [inputRef, focusNonce])

  // Walk the board in canvas (filename) order so the "X of Y" counter and
  // Up/Down navigation step through matches in the same order pages render.
  // Otherwise "Next" could send the canvas backwards through the pages.
  const matchingIds = useMemo(() => {
    if (!boardFileSearchQuery) return []
    const query = boardFileSearchQuery.toLowerCase()
    const nameById = new Map<string, string>()
    for (const item of [...documents, ...connectors]) {
      nameById.set(item.id, item.name.toLowerCase())
    }
    const seen = new Set<string>()
    const ordered = sortByFilename<DocumentPosition>(
      (selectedDocuments as DocumentPosition[]).filter((d: DocumentPosition) => {
        if (seen.has(d.document_id)) return false
        seen.add(d.document_id)
        return true
      }),
      documents,
      connectors,
      renamedDocNames,
    )
    const ids: string[] = []
    for (const doc of ordered) {
      const name = nameById.get(doc.document_id)
      if (name && name.includes(query)) ids.push(doc.document_id)
    }
    return ids
  }, [boardFileSearchQuery, documents, connectors, selectedDocuments, renamedDocNames])

  // Snap the index back into range when the match list shrinks (e.g. after a
  // query edit). Query changes already reset to 0 via setBoardFileSearchQuery.
  useEffect(() => {
    if (matchingIds.length === 0) {
      if (currentIndex !== 0) setCurrentIndex(0)
    } else if (currentIndex >= matchingIds.length) {
      setCurrentIndex(0)
    }
  }, [matchingIds.length, currentIndex, setCurrentIndex])

  const goToNext = () => {
    if (matchingIds.length === 0) return
    setCurrentIndex((currentIndex + 1) % matchingIds.length)
  }

  const goToPrevious = () => {
    if (matchingIds.length === 0) return
    setCurrentIndex((currentIndex - 1 + matchingIds.length) % matchingIds.length)
  }

  const handleClose = () => {
    setBoardFileSearchQuery('')
    onClose?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
    if (e.key === 'Enter' && matchingIds.length > 0) {
      e.preventDefault()
      if (e.shiftKey) goToPrevious()
      else goToNext()
    }
  }

  const hasQuery = boardFileSearchQuery.length > 0
  const hasMatches = matchingIds.length > 0
  const canStep = matchingIds.length > 1

  const stepButton =
    'flex items-center justify-center size-7 rounded-lg text-muted-foreground transition-colors ' +
    'hover:bg-[#f0f1f4] dark:hover:bg-dark-muted hover:text-foreground ' +
    'disabled:opacity-40 disabled:pointer-events-none'

  return (
    <div
      {...{ [BOARD_FILE_SEARCH_ATTR]: '' }}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-lg px-3 py-2 min-w-[200px] max-w-[420px]',
        className
      )}
    >
      <Search
        className={cn(
          'w-4 h-4 shrink-0 transition-colors',
          hasQuery ? 'text-[#0e7490]' : 'text-gray-400 dark:text-gray-500'
        )}
        aria-hidden
      />
      <Input
        ref={inputRef}
        type="text"
        value={boardFileSearchQuery}
        onChange={(e) => setBoardFileSearchQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('search_files')}
        className="border-0 bg-transparent p-0 h-8 min-w-0 flex-1 text-[15px] placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
        aria-label={t('search_files')}
      />

      {/* The counter and the stepper hold their slots from the first keystroke.
          They used to appear only once there was a query / more than one match,
          which resized the field under the caret while the user was typing. */}
      {hasQuery && (
        <span
          className={cn(
            'text-xs tabular-nums whitespace-nowrap shrink-0',
            hasMatches ? 'text-gray-500 dark:text-dark-text-muted' : 'text-amber-600 dark:text-amber-500'
          )}
          aria-live="polite"
        >
          {hasMatches
            ? t('search.match_position', {
                current: currentIndex + 1,
                total: matchingIds.length,
              })
            : t('search.no_matches')}
        </span>
      )}

      <div className="h-5 w-px bg-[#e8eaee] dark:bg-dark-border shrink-0" />

      <button
        type="button"
        onClick={goToPrevious}
        disabled={!canStep}
        className={stepButton}
        title={t('search.previous_match_title')}
        aria-label={t('search.previous_match_title')}
      >
        <ChevronUp className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={goToNext}
        disabled={!canStep}
        className={stepButton}
        title={t('search.next_match_title')}
        aria-label={t('search.next_match_title')}
      >
        <ChevronDown className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={handleClose}
        className={stepButton}
        title={t('search.close')}
        aria-label={t('search.close')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
